// Smart Sash Admin Panel - User Store (v2)
import {
  CreateUserRequest,
  PaginationMetadata,
  UpdateUserRequest,
  UserCredentials,
  UserDTO,
  UserFilterOptions,
} from '@/models';
import { userService } from '@/services/api/userService';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface UserState {
  users: UserDTO[];
  selectedUser: UserDTO | null;
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  error: string | null;
  lastCreatedCredentials: UserCredentials | null;
  fetchUsers: (filters?: UserFilterOptions) => Promise<void>;
  fetchUserById: (institutionId: string, userId: string) => Promise<void>;
  createUser: (data: CreateUserRequest) => Promise<UserDTO>;
  updateUser: (institutionId: string, userId: string, data: UpdateUserRequest) => Promise<void>;
  deleteUser: (institutionId: string, userId: string) => Promise<void>;
  regenerateTempPassword: (institutionId: string, userId: string) => Promise<UserCredentials>;
  setSelectedUser: (user: UserDTO | null) => void;
  clearError: () => void;
  clearCredentials: () => void;
}

export const useUserStore = create<UserState>()(
  devtools(
    (set) => ({
      users: [],
      selectedUser: null,
      pagination: null,
      isLoading: false,
      error: null,
      lastCreatedCredentials: null,
      fetchUsers: async (filters?: UserFilterOptions) => {
        set({ isLoading: true, error: null });
        try {
          const response = await userService.getAll({ page: 1, pageSize: 10000 }, filters);
          set({ users: response.data, pagination: response.pagination, isLoading: false });
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to fetch users'), isLoading: false });
          throw error;
        }
      },
      fetchUserById: async (institutionId: string, userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const user = await userService.getById(institutionId, userId);
          set({ selectedUser: user, isLoading: false });
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to fetch user'), isLoading: false });
          throw error;
        }
      },
      createUser: async (data: CreateUserRequest) => {
        set({ isLoading: true, error: null });
        try {
          const result = await userService.createUser(data);
          set((state) => ({
            users: [...state.users, result.user],
            lastCreatedCredentials: result.credentials,
            isLoading: false,
          }));
          return result.user;
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to create user'), isLoading: false });
          throw error;
        }
      },
      updateUser: async (institutionId: string, userId: string, data: UpdateUserRequest) => {
        set({ isLoading: true, error: null });
        try {
          const updatedUser = await userService.update(institutionId, userId, data);
          set((state) => ({
            users: state.users.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
            selectedUser:
              state.selectedUser?.id === updatedUser.id ? updatedUser : state.selectedUser,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to update user'), isLoading: false });
          throw error;
        }
      },
      deleteUser: async (institutionId: string, userId: string) => {
        set({ isLoading: true, error: null });
        try {
          await userService.delete(institutionId, userId);
          set((state) => ({
            users: state.users.filter((u) => u.id !== userId),
            selectedUser: state.selectedUser?.id === userId ? null : state.selectedUser,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to delete user'), isLoading: false });
          throw error;
        }
      },
      regenerateTempPassword: async (institutionId: string, userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const credentials = await userService.regenerateTempPassword(institutionId, userId);
          set({ lastCreatedCredentials: credentials, isLoading: false });
          return credentials;
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to regenerate password'),
            isLoading: false,
          });
          throw error;
        }
      },
      setSelectedUser: (user: UserDTO | null) => set({ selectedUser: user }),
      clearError: () => set({ error: null }),
      clearCredentials: () => set({ lastCreatedCredentials: null }),
    }),
    { name: 'UserStore' }
  )
);
